import React, { Fragment, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Calendar, Navigate, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import TimeGrid from 'react-big-calendar/lib/TimeGrid';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';
import { getCalendarGrid } from './constants';

const localizer = momentLocalizer(moment);
const DragAndDropCalendar = withDragAndDrop(Calendar);

export default function CalendarCustomView({
    events = [],
    info,
    components,
    onSelectEvent,
    eventPropGetter,
    onNavigate,
    onShowMore,
    slotPropGetter,
    onSelectSlot,
    onEventDrop,
    onEventResize,
    draggableAccessor,
}) {
    const startOfWeek = moment().startOf('week');
    const endOfWeek = moment().endOf('week');

    const firstDate = info?.firstDate ? moment(info.firstDate) : startOfWeek;
    const lastDate = info?.lastDate ? moment(info.lastDate) : endOfWeek;

    const dayCount = lastDate.diff(firstDate, 'days') + 1;

    function MyThreeDayView({
        date,
        localizer,
        max = localizer.endOf(new Date(), 'day'),
        min = localizer.startOf(new Date(), 'day'),
        scrollToTime = new Date(1972, 0, 1, 6),
        ...props
    }) {
        const currRange = useMemo(() => MyThreeDayView.range(date, { localizer }), [date, localizer]);

        return (
            <TimeGrid
                {...props}
                date={date}
                range={currRange}
                eventOffset={15}
                localizer={localizer}
                max={max}
                min={min}
                scrollToTime={scrollToTime}
            />
        );
    }

    MyThreeDayView.propTypes = {
        date: PropTypes.instanceOf(Date).isRequired,
        localizer: PropTypes.object,
        max: PropTypes.instanceOf(Date),
        min: PropTypes.instanceOf(Date),
        scrollToTime: PropTypes.instanceOf(Date),
    };

    MyThreeDayView.navigate = (date, action, { localizer }) => {
        switch (action) {
            case Navigate.PREVIOUS:
                return localizer.add(date, -(dayCount - 1), 'day');
            case Navigate.NEXT:
                return localizer.add(date, dayCount - 1, 'day');
            default:
                return date;
        }
    };

    MyThreeDayView.title = (date) => {
        return `  `;
    };

    MyThreeDayView.range = (date, { localizer }) => {
        const start = moment.utc(firstDate).local().startOf('day');
        const end = moment.utc(lastDate).local().startOf('day');

        const range = [];
        let current = start.clone();

        while (current.isSameOrBefore(end, 'day')) {
            range.push(current.toDate());
            current.add(1, 'day');
        }

        return range;
    };

    const { defaultDate, views } = useMemo(
        () => ({
            defaultDate: new Date(),
            views: {
                customDays: MyThreeDayView,
            },
        }),
        []
    );

    let userSettings = null;
    try { userSettings = JSON.parse(localStorage.getItem('userSettings')); } catch (e) { /* corrupt data */ }

    const { scrollToTime } = useMemo(
        () => ({
            scrollToTime: new Date(1972, 0, 1, 6),
        }),
        []
    );

    const interval = info?.slot || userSettings?.slot;
    const { step, timeslots, groupMinutes } = getCalendarGrid(interval);

    const calendarRef = React.useRef(null);

    // Programmatically scroll the time grid to 6 AM after mount.
    // scrollToTime prop doesn't work reliably with this custom TimeGrid setup
    // because the outer div (600px) clips the calendar (900px).
    React.useEffect(() => {
        const el = calendarRef.current;
        if (!el) return;

        const timeContent = el.querySelector('.rbc-time-content');
        if (!timeContent) return;

        // Each row spans `groupMinutes`; scroll past the rows before 6 AM.
        const groups = el.querySelectorAll('.rbc-time-gutter .rbc-timeslot-group');
        if (groups.length > 0) {
            const groupHeight = groups[0].offsetHeight;
            timeContent.scrollTop = groupHeight * ((6 * 60) / groupMinutes);
        }
    }, [info?.firstDate, info?.lastDate, groupMinutes]);

    return (
        <Fragment>
            <div ref={calendarRef} style={{ height: '600px' }}>
                <DragAndDropCalendar
                    className="calendar-container"
                    slotPropGetter={slotPropGetter}
                    components={components}
                    resizable
                    selectable
                    localizer={localizer}
                    startAccessor="start"
                    endAccessor="end"
                    popup
                    style={{ height: 900, overflow: 'auto' }}
                    step={step}
                    timeslots={timeslots}
                    scrollToTime={scrollToTime}
                    defaultDate={defaultDate}
                    defaultView="customDays"
                    events={events}
                    views={views}
                    onSelectEvent={onSelectEvent}
                    eventPropGetter={eventPropGetter}
                    onNavigate={onNavigate}
                    onShowMore={onShowMore}
                    onSelectSlot={(e, j) => onSelectSlot(e, j)}
                    onEventDrop={onEventDrop}
                    onEventResize={onEventResize}
                    draggableAccessor={draggableAccessor}
                    resizableAccessor={draggableAccessor}
                    formats={{
                        timeGutterFormat: (date, culture, localizer) => {
                            if (groupMinutes <= 30) {
                                return localizer.format(date, 'h:mm a', culture);
                            } else {
                                return localizer.format(date, 'h a', culture);
                            }
                        },
                    }}
                />
            </div>
        </Fragment>
    );
}
